import React, { useCallback, useEffect, useRef, useState } from "react";

const MIN_WIDTH = 300;
const MAX_WIDTH = 800;
const DEFAULT_WIDTH = 400;

// Chave global para armazenar a largura compartilhada entre todos os sidebars
const GLOBAL_STORAGE_KEY = "sidebar_width_global";

/**
 * Hook para gerenciar o redimensionamento de sidebars
 * Usa uma largura global compartilhada entre todos os tipos de sidebar
 */
export const useResizableSidebar = (sidebarType: string | null) => {
  const [width, setWidth] = useState<number>(DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(DEFAULT_WIDTH);

  // Carregar largura global salva do localStorage quando o sidebarType muda
  useEffect(() => {
    if (!sidebarType) {
      return;
    }

    const savedWidth = localStorage.getItem(GLOBAL_STORAGE_KEY);

    if (savedWidth) {
      const parsedWidth = parseInt(savedWidth, 10);
      // Validar que está dentro dos limites
      if (parsedWidth >= MIN_WIDTH && parsedWidth <= MAX_WIDTH) {
        setWidth(parsedWidth);
        startWidthRef.current = parsedWidth;
      } else {
        // Se estiver fora dos limites, usar valor padrão
        setWidth(DEFAULT_WIDTH);
        startWidthRef.current = DEFAULT_WIDTH;
      }
    } else {
      // Se não houver valor salvo, usar padrão
      setWidth(DEFAULT_WIDTH);
      startWidthRef.current = DEFAULT_WIDTH;
    }
  }, [sidebarType]);

  // Salvar largura global no localStorage
  const saveWidth = useCallback((newWidth: number) => {
    localStorage.setItem(GLOBAL_STORAGE_KEY, newWidth.toString());
  }, []);

  // Iniciar redimensionamento
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = width;
    },
    [width],
  );

  // Resetar para largura padrão
  const resetToDefault = useCallback(() => {
    setWidth(DEFAULT_WIDTH);
    startWidthRef.current = DEFAULT_WIDTH;
    localStorage.setItem(GLOBAL_STORAGE_KEY, DEFAULT_WIDTH.toString());
  }, []);

  // Handler para duplo clique
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      resetToDefault();
    },
    [resetToDefault],
  );

  // Redimensionar durante o arraste
  useEffect(() => {
    if (!isResizing) return;

    let currentWidth = startWidthRef.current;

    const handleMouseMove = (e: MouseEvent) => {
      // Quando arrasta para a direita (e.clientX aumenta), diff é positivo e aumenta a largura
      // Quando arrasta para a esquerda (e.clientX diminui), diff é negativo e diminui a largura
      const diff = e.clientX - startXRef.current;
      const newWidth = Math.max(
        MIN_WIDTH,
        Math.min(MAX_WIDTH, startWidthRef.current + diff),
      );
      currentWidth = newWidth;
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      // Salvar largura final global no localStorage
      localStorage.setItem(GLOBAL_STORAGE_KEY, currentWidth.toString());
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, sidebarType]);

  return {
    width,
    isResizing,
    handleMouseDown,
    handleDoubleClick,
    resetToDefault,
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
  };
};
